import {
  type AffineUserInfo,
  isRemovedUserInfo,
  UserListProvider,
  UserProvider,
} from '@blocksuite/affine-shared/services';

import { EditorHostKey } from '../../shared/editor-host-context.js';
import type { GanttAssigneeVariant, GanttBarDisplayKind } from '../define.js';
import type { GanttSingleView } from '../gantt-view-manager.js';

/**
 * Reads the text representation of a cell that the user understands —
 * delegates to the property's own `stringValue$` so each column type
 * (text, date, select, number, ...) formats itself consistently with
 * the table/kanban views. Never returns null: an empty cell renders
 * as an empty string so the label layout doesn't jump.
 */
export function readCellText(
  view: GanttSingleView,
  rowId: string,
  propertyId: string
): string {
  return view.cellGetOrCreate(rowId, propertyId).stringValue$.value ?? '';
}

/**
 * Resolved payload for a single assignee on a bar chip. Carries both
 * the display name and the avatar URL so the renderer doesn't have to
 * hit the user services again while painting. `avatarUrl === null`
 * falls back to initials inside the same avatar slot.
 */
export type ResolvedBarAssignee = {
  readonly id: string;
  readonly name: string;
  readonly avatarUrl: string | null;
  readonly initials: string;
  readonly isRemoved: boolean;
};

/**
 * Resolved value for a single bar-display chip. The renderer maps
 * these directly to chip DOM so nothing downstream reads cell data
 * again — keeps the hot render path free of signal reads per chip.
 *
 * Empty state: `text` null AND `avatarInitials` null AND
 * `members` null ⇒ no chip. Renderers must filter these out before
 * iterating.
 */
export type ResolvedBarChip = {
  kind: GanttBarDisplayKind;
  /** Text payload for name, status, progress, dates — truncation target. */
  text: string | null;
  /** Present on assignee chips when the variant asks for an avatar. */
  avatarInitials: string | null;
  /** Present on status chips when the select option has a colour. */
  statusColor: string | null;
  /**
   * Present ONLY on assignee chips, and only when the assignee
   * variant requests an avatar. Carries the ordered list of resolved
   * members so the renderer can paint real avatar images (with
   * initials fallback) and a comma-joined name list — mirroring the
   * member cell in the table view instead of showing the raw
   * member-id string the property's `stringValue$` returns.
   */
  members: ResolvedBarAssignee[] | null;
};

/**
 * Derive the first two uppercase initials for the assignee avatar
 * placeholder. "Corvin Müller" → "CM"; "Acme Corp" → "AC". Falls
 * back to the first glyph of the string when only one word is
 * present so the avatar always has *something* to render.
 */
function initialsOf(name: string): string {
  const trimmed = name.trim();
  if (!trimmed) return '';
  const words = trimmed.split(/\s+/);
  if (words.length === 1) return words[0]!.slice(0, 2).toUpperCase();
  return (words[0]![0]! + words[words.length - 1]![0]!).toUpperCase();
}

function normalizeAvatarUrl(url: string | null | undefined): string | null {
  if (!url) return null;
  const trimmed = url.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function pickName(info: AffineUserInfo | null | undefined): string | null {
  if (!info || isRemovedUserInfo(info)) return null;
  return info.name ?? null;
}

function pickAvatar(info: AffineUserInfo | null | undefined): string | null {
  if (!info || isRemovedUserInfo(info)) return null;
  return normalizeAvatarUrl(info.avatar);
}

/**
 * Resolve a single member id to its display payload. The fallback
 * order mirrors the member cell's own lookup chain (primary user
 * service → user-list cache → bare id) so an assignee chip can never
 * be "more ignorant" than the underlying cell — otherwise the bar
 * would show `user-abc123` while the same row's label column paints
 * the real name.
 */
function resolveMember(view: GanttSingleView, id: string): ResolvedBarAssignee {
  // The user service is registered on the editor-host's std container
  // (via CloudViewExtension), not on the data-source provider's scope.
  // `view.serviceGet(UserProvider)` therefore returns null — we have to
  // hop through the EditorHost, which the database block registers on
  // the data source under the `'editor-host'` identifier.
  const host = view.serviceGet(EditorHostKey);
  const userService = host?.std.getOptional(UserProvider);
  const userListService = host?.std.getOptional(UserListProvider);

  // `userInfo$` stays null until the service resolves — same pattern
  // as the member cell's `useMemberInfo` hook, which calls
  // `revalidateUserInfo` in a useEffect. The service caches by id so
  // repeated calls are a no-op after the first resolve.
  userService?.revalidateUserInfo(id);

  const primary = userService?.userInfo$(id).value ?? null;
  // Fallback for the current user — `userInfo$` for self can arrive
  // later than the already-known `currentUserInfo$`, so check both
  // before giving up and painting the raw id.
  const currentUser = userService?.currentUserInfo$.value ?? null;
  const userInfo =
    primary ?? (currentUser && currentUser.id === id ? currentUser : null);
  const listUser = userListService?.users$.value.find(u => u.id === id) ?? null;

  const isRemoved = userInfo ? isRemovedUserInfo(userInfo) : false;
  const name = isRemoved
    ? 'Deleted user'
    : (pickName(userInfo) ?? pickName(listUser) ?? id);
  const avatarUrl = isRemoved
    ? null
    : (pickAvatar(userInfo) ?? pickAvatar(listUser));

  return {
    id,
    name,
    avatarUrl,
    initials: initialsOf(name),
    isRemoved,
  };
}

/**
 * Produce the ready-to-render chip for one display item. Returns
 * `null` when the role column isn't configured on the view (e.g.
 * status picked in the items list but never mapped to a column), so
 * the caller's filter step drops silent gaps.
 */
export function resolveBarChip(
  view: GanttSingleView,
  rowId: string,
  kind: GanttBarDisplayKind,
  variant: GanttAssigneeVariant | undefined
): ResolvedBarChip | null {
  const colId = view.resolveBarItemColumn(kind);
  if (!colId) return null;

  if (kind === 'assignee') {
    // Member cells store raw IDs in `value$`; the property's
    // `stringValue$` joins them with commas which looks fine in a
    // filter expression but meaningless as a visible bar label. Read
    // the raw array and resolve each id to name + avatar via the
    // same user services the member cell uses.
    const raw = view.cellGetOrCreate(rowId, colId).value$.value as
      | readonly string[]
      | undefined;
    if (!raw || raw.length === 0) return null;

    const members = raw.map(id => resolveMember(view, id));
    const v: GanttAssigneeVariant = variant ?? 'both';
    const showAvatar = v === 'avatar' || v === 'both';
    const showName = v === 'name' || v === 'both';
    const nameText = members.map(m => m.name).join(', ');

    return {
      kind,
      text: showName ? nameText : null,
      avatarInitials: showAvatar ? members[0]!.initials : null,
      statusColor: null,
      members: showAvatar ? members : null,
    };
  }

  if (kind === 'status') {
    const text = readCellText(view, rowId, colId).trim();
    if (!text) return null;
    // `jsonValue$` on a select cell carries the option metadata
    // (id + colour) so we can tint the chip. The cell may return
    // a plain string on legacy data — tolerate both.
    const json = view.cellGetOrCreate(rowId, colId).jsonValue$.value as
      | string
      | { value?: { color?: string } }
      | null
      | undefined;
    const color =
      typeof json === 'object' && json && 'value' in json
        ? (json.value?.color ?? null)
        : null;
    return {
      kind,
      text,
      avatarInitials: null,
      statusColor: color,
      members: null,
    };
  }

  if (kind === 'progress') {
    const text = readCellText(view, rowId, colId).trim();
    if (!text) return null;
    // Progress cells already format as "42%" via their stringValue$;
    // guard for raw 0..1 floats written by older rows.
    const numeric = Number(text);
    const pretty =
      Number.isFinite(numeric) && !text.includes('%')
        ? `${Math.round(numeric * (numeric <= 1 ? 100 : 1))}%`
        : text;
    return {
      kind,
      text: pretty,
      avatarInitials: null,
      statusColor: null,
      members: null,
    };
  }

  const text = readCellText(view, rowId, colId).trim();
  if (!text) return null;
  return {
    kind,
    text,
    avatarInitials: null,
    statusColor: null,
    members: null,
  };
}
